import React, { useState, useEffect, useCallback } from 'react';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { getMe, getBuildings } from './services/api';
import Login from './pages/Login';
import Register from './pages/Register';
import Setup from './pages/Setup';
import Dashboard from './pages/Dashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import LandingPage from './pages/LandingPage';
import useIdleLogout from './hooks/useIdleLogout';

function getPageFromUrl() {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);
  if (path === '/login') return 'login';
  if (path === '/register' || params.get('ref')) return 'register';
  return 'landing';
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const [page, setPage] = useState(getPageFromUrl);
  const [idleWarning, setIdleWarning] = useState(false);
  const [idleCountdown, setIdleCountdown] = useState(60);

  function navigateTo(newPage) {
    const paths = { landing: '/', login: '/login', register: '/register' };
    window.history.pushState({ page: newPage }, '', paths[newPage] || '/');
    setPage(newPage);
  }

  useEffect(() => {
    function handlePopState(e) {
      if (e.state && e.state.page) {
        setPage(e.state.page);
      } else {
        setPage(getPageFromUrl());
      }
    }
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('rentos_token');
    if (token) {
      getMe()
        .then(res => { setUser(res.data); return getBuildings(); })
        .then(res => { setNeedsSetup(res.data.length === 0); })
        .catch(() => { localStorage.removeItem('rentos_token'); setUser(null); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Countdown ticker while warning toast is visible
  useEffect(() => {
    if (!idleWarning) return;
    setIdleCountdown(60);
    const tick = setInterval(() => {
      setIdleCountdown(prev => {
        if (prev <= 1) { clearInterval(tick); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [idleWarning]);

  const handleLogin = async (userData, token) => {
    localStorage.setItem('rentos_token', token);
    setUser(userData);
    try {
      const res = await getBuildings();
      setNeedsSetup(res.data.length === 0);
    } catch (e) {
      setNeedsSetup(false);
    }
  };

  const handleRegister = async (userData, token) => {
    localStorage.setItem('rentos_token', token);
    setUser(userData);
    setNeedsSetup(true);
  };

  const handleSetupComplete = () => setNeedsSetup(false);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('rentos_token');
    setUser(null);
    setIdleWarning(false);
    setNeedsSetup(false);
    setPage('landing');
    window.history.pushState({ page: 'landing' }, '', '/');
  }, []);

  const handleIdleWarn = useCallback(() => setIdleWarning(true), []);
  const handleIdleWarnClear = useCallback(() => setIdleWarning(false), []);

  // Hook only fires when a landlord is logged in
  useIdleLogout(
    user ? handleLogout : null,
    user ? handleIdleWarn : null,
    user ? handleIdleWarnClear : null
  );

  if (loading) {
    return (
      <>
        <div style={{
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', height: '100vh',
          color: '#64748b', fontSize: 14
        }}>Loading RentOS...</div>
      </>
    );
  }

  if (window.location.pathname === '/privacy-policy') {
    return <PrivacyPolicy />;
  }

  return (
    <>
      {/* Idle logout warning toast */}
      {user && idleWarning && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          background: '#1e293b', color: 'white',
          padding: '14px 24px', borderRadius: 10,
          boxShadow: '0 8px 30px rgba(0,0,0,0.25)',
          display: 'flex', alignItems: 'center', gap: 16,
          fontSize: 14, fontWeight: 500,
          border: '1px solid rgba(255,255,255,0.1)',
          minWidth: 320
        }}>
          <span style={{ fontSize: 20 }}>⏱</span>
          <span style={{ flex: 1 }}>
            Logging out in{' '}
            <strong style={{ color: idleCountdown <= 15 ? '#f87171' : '#fbbf24' }}>
              {idleCountdown}s
            </strong>
            {' '}due to inactivity.
          </span>
          <button
            onClick={handleIdleWarnClear}
            style={{
              background: '#1a56db', color: 'white',
              border: 'none', borderRadius: 6,
              padding: '6px 14px', fontSize: 13,
              fontWeight: 600, cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            Stay Logged In
          </button>
        </div>
      )}

      {/* Authenticated routes */}
      {user && needsSetup && (
        <Setup user={user} onComplete={handleSetupComplete} onLogout={handleLogout} />
      )}
      {user && !needsSetup && (
        <Dashboard user={user} onLogout={handleLogout} />
      )}

      {/* Public routes */}
      {!user && page === 'login' && (
        <Login
          onLogin={handleLogin}
          onRegister={() => navigateTo('register')}
          onBack={() => navigateTo('landing')}
        />
      )}
      {!user && page === 'register' && (
        <Register
          onRegister={handleRegister}
          onBack={() => navigateTo('login')}
        />
      )}
      {!user && page === 'landing' && (
        <LandingPage
          onLogin={() => navigateTo('login')}
          onRegister={() => navigateTo('register')}
        />
      )}

      {/* Analytics rendered once, not duplicated across branches */}
      <Analytics />
      <SpeedInsights />
    </>
  );
}

export default App;
